import {createSelector} from '@store/@common';
import {Set} from 'immutable';
import KpiObservationStore from '@store/kpi/KpiObservationStore';
import {kpisById} from './kpi.selectors';
import {KpiChartOption} from '@store/@types/kpi';
import {SkillId} from 'records/Skill';
import CoachingPlan from 'records/CoachingPlan';
import {skillColorByIdx} from 'utils/skill-color-by-idx';

const baseStore = createSelector(KpiObservationStore);

export const kpiObservations = createSelector([baseStore], s => s.kpiObservations);
export const pageCount = createSelector([baseStore], s => Math.ceil(s.totalResults / s.pageSize));
export const kpiRequestsByUser = createSelector([baseStore], s => s.kpiRequestsByUser);
export const coachingKpisByUser = createSelector([baseStore], s => s.coachingKpisByUser);

export const getChartOptions = createSelector(
  [kpiObservations, kpisById],
  (observations, kpis) => (plan: CoachingPlan) => {
    if (observations.isEmpty() || kpis.isEmpty()) return [];
    const [firstSkill, secondSkill] = plan.contentDetails;
    const bothSkillNames = plan.getSkillNames('/');

    // Return a group name, depending on which skills the kpi has
    const getGroup = (skills: Set<SkillId>) => {
      if (skills.has(firstSkill?.skillId) && skills.has(secondSkill?.skillId)) {
        return bothSkillNames;
      }
      if (skills.has(firstSkill?.skillId)) return firstSkill.skillName;
      if (skills.has(secondSkill?.skillId)) return secondSkill.skillName;
      return 'Unrelated';
    };

    const obsByKpi = observations.groupBy(obs => obs.kpiId);
    const options = obsByKpi.map((obs, kpiId) => {
      const kpi = kpis.get(kpiId)!;
      return {
        name: kpi.name,
        kpiId: kpi.id,
        numericType: kpi.numericType,
        group: getGroup(kpi.skillIds),
        goal: kpi.value,
        obsCount: obs.size,
      } as KpiChartOption;
    });

    const groupOrder = [bothSkillNames, firstSkill?.skillName, secondSkill?.skillName, 'Unrelated'];
    return options
      .toList()
      .sortBy(opt => groupOrder.indexOf(opt.group))
      .map((opt, i) => ({...opt, color: skillColorByIdx(i)}))
      .toJS() as KpiChartOption[];
  }
);
